import { Video, MapPin, Lock } from 'lucide-react'
import type { CalendarEvent } from '@trackmind/core'
import { cn } from '@/lib/utils'

/**
 * One meeting in a day timeline.
 *
 * Deliberately shaped like the schedule's task rows — same time column width,
 * same height, same padding — so a day reads as one list rather than two
 * interleaved systems. What marks it as external is the left colour bar (the
 * calendar's own colour) and the absent completion circle: you don't tick a
 * meeting off, it just happens.
 *
 * A `busy`-privacy calendar has no title to show. Rather than render an empty
 * row it says "Busy", which is the honest description of what's known.
 */
export function CalendarEventRow({
  event,
  color,
  compact = false,
  subdued = false,
}: {
  event: CalendarEvent
  color?: string | null
  compact?: boolean
  /** Ranked below another meeting at the same time — see CalendarEventGroup. */
  subdued?: boolean
}) {
  const isPrivate = event.title === null
  const label = isPrivate ? 'Busy' : event.title
  const isVideoCall = Boolean(event.location && /^https?:\/\//.test(event.location))

  // Same 12-hour shape the schedule rows use, so the two line up in one
  // timeline rather than looking like two different systems.
  const time = event.all_day ? 'All day' : formatClock(new Date(event.start))

  return (
    <div
      className={cn(
        'relative flex items-center gap-2 rounded-lg border border-dashed bg-muted/30 md:gap-4 md:px-4',
        compact ? 'px-2 py-1.5' : 'px-3 py-2.5 md:py-3',
        subdued && 'opacity-70',
      )}
      style={{
        borderLeftWidth: '4px',
        borderLeftStyle: 'solid',
        borderLeftColor: color ?? 'hsl(var(--muted-foreground))',
      }}
      title={isPrivate ? 'Busy (title hidden for this calendar)' : (event.title ?? undefined)}
    >
      <div className="w-16 shrink-0 whitespace-nowrap text-right font-mono text-xs text-muted-foreground md:w-20">
        {time}
      </div>

      {/* No completion control: an external event isn't ours to tick off. */}
      <div className="w-6 shrink-0 flex items-center justify-center">
        {isPrivate ? (
          <Lock className="h-3.5 w-3.5 text-muted-foreground" />
        ) : isVideoCall ? (
          <Video className="h-4 w-4 text-muted-foreground" />
        ) : (
          <span className="h-2 w-2 rounded-full bg-muted-foreground/50" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={cn('truncate text-sm', isPrivate && 'italic text-muted-foreground')}>
            {label}
          </span>
        </div>
        {/* Location is desktop-only: on a phone the title needs the width. */}
        {event.location && !isVideoCall && (
          <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <MapPin className="h-3 w-3" />
            <span className="truncate">{event.location}</span>
          </div>
        )}
      </div>

      {!event.all_day && (
        <div className="shrink-0 text-xs text-muted-foreground">
          {formatSpan(event.start, event.end)}
        </div>
      )}
    </div>
  )
}

function formatClock(date: Date): string {
  const hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  return `${hours % 12 || 12}:${minutes} ${ampm}`
}

function formatSpan(start: string, end: string): string {
  const minutes = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60000)
  if (!Number.isFinite(minutes) || minutes <= 0) return ''
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`
}
